import * as R from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getColorMap } from '../utils/colors';
import { awaitJSON, CreativeLambdaFetch } from '../utils/fetch-utils';
import { AppDispatch, RootState } from '.';
import { useSetError } from './modals';
import { makeReducer } from './utils';

const ROOT = 'creative' as const;
const SET_CREATIVE_MAP = `${ROOT}/SET_CREATIVE_MAP`;
const SET_CREATIVE_MAP_IF_UNSET = `${ROOT}/SET_CREATIVE_MAP_IF_UNSET`;

// The `/creative/` fetch doesn't vary by mediaTypes (that's a client-side filter applied to the
// response below), so every useCreativeMap instance for the same company/testParams hits the
// identical endpoint. Multiple components mount this hook at once (StreamingCreatives,
// useWorkspaceOptions, Generate, Cart, ViewPlacement), so dedupe concurrent fetches here rather
// than relying on each hook instance's own local state.
const inFlightCreativeFetches: Record<string, Promise<CreativeData>> = {};
export interface CreativeTimelineDateRange {
  startDate: string;
  endDate: string;
}
export interface CreativeTimeline {
  current: Partial<CreativeTimelineDateRange>;
  future: Partial<CreativeTimelineDateRange>;
  past: Partial<CreativeTimelineDateRange>;
  ranges: CreativeTimelineDateRange[];
}

export type TimelineKey = 'streaming' | 'linear';
export type MediaType = 'streaming' | 'audio' | 'linear' | 'display';
interface CreativeDataItem {
  isci: string;
  liveLinear: boolean;
  liveStreaming: boolean;
  name: string;
  modelEntry?: number;
  concept: string;
  length: number;
  language: 'English' | 'Spanish' | 'French';
  avail: 'N' | 'L';
  file: string;
  clickthroughUrl?: string;
  media_types: MediaType[];
  retired: boolean;
  creativeTags: Record<string, string[]>;
  timeline: Record<TimelineKey, CreativeTimeline>;
  mostRecentDateRange?: { startDate: string; endDate: string };
  flashtalking_id?: number;
  flashtalking_pixel_id?: number;
  dimensions?: string;
}

type CreativeData = Record<string, CreativeDataItem>;

type CreativeReduxState = Record<string, CreativeData>;

const creativeMapSelector =
  (company: string) =>
  (state: RootState): CreativeData | undefined =>
    state[ROOT][company];

export type CreativeMapItem = Omit<CreativeDataItem, 'timeline'> & {
  ranges: CreativeTimelineDateRange[];
  timeline?: Record<TimelineKey, CreativeTimeline>;
};
export type CreativeMap = Record<string, CreativeMapItem>;

export type IsciLiveOnDate = (args: { isci: string; date: string }) => boolean;

export const isciLiveOnDate =
  (creativeMap: CreativeMap): IsciLiveOnDate =>
  ({ isci, date }) =>
    R.any(
      range => range.startDate <= date && (!range.endDate || range.endDate >= date),
      creativeMap[isci]?.ranges || []
    );

interface UseCreativeMapProps {
  company: string;
  streaming?: boolean;
  mediaTypes: MediaType[];
  testParams?: { channel?: string; pageID?: string };
  refresh?: boolean;
  setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UseCreativeMapPayload {
  creativeMap?: CreativeMap;
  colorMap: Record<string, string>;
  isciLiveOnDate: IsciLiveOnDate;
  loaded: boolean;
}

export const useCreativeMap = ({
  company,
  streaming = true,
  mediaTypes,
  testParams = {},
  refresh,
  setRefresh
}: UseCreativeMapProps): UseCreativeMapPayload => {
  const dispatch = useDispatch<AppDispatch>();
  const setError = useSetError();
  const creativeData = useSelector(creativeMapSelector(company));
  const testParamsKey = JSON.stringify(testParams);
  // Callers pass a fresh object literal each render, so key the identity on its serialized form.
  const stableTestParams = useMemo<typeof testParams>(
    () => JSON.parse(testParamsKey),
    [testParamsKey]
  );
  const [loaded, setLoaded] = useState<boolean>(false);

  const [creativeMap, colorMap] = useMemo(() => {
    // There are two steps to this process. First we need to turn the payload inside-out a bit to
    // group by streaming vs linear. Second, we need to sort the output in order to get the colors
    // in a consistent order. The trick here is realizing that there may be many non-live creatives
    // that are not likely to be touched. When we run out of colors it starts randomly generating
    // them, which usually produces ugly colors. So we want the most-likely-to-be-used creatives to
    // get the first pick of colors. The user will likely be editing rotations on creatives that are
    // currently live. They might look into the future and set rotations on creatives that haven't
    // started. They likely won't be editing creatives that are not live and aren't set to become
    // live, but they might have a rotation that currently runs a now-non-live creative, so it needs
    // a color. So group them by current, future, and past. Within that order, sort by ISCI. Then
    // merge the lists and allocate the colors in order. We can also do this separately for
    // linear/streaming
    const groupingMap: Record<'future' | 'current' | 'past' | 'never', string[]> = {
      future: [],
      current: [],
      past: [],
      never: []
    };

    const builtCreativeMap: CreativeMap = {};

    const type: TimelineKey = streaming ? 'streaming' : 'linear';

    for (const { timeline, isci, ...rest } of R.values(creativeData || {})) {
      if (R.pipe(R.intersection(mediaTypes), R.length, R.not)(rest.media_types)) {
        continue;
      }
      const { ranges } = timeline[type];
      builtCreativeMap[isci] = {
        ...rest,
        isci,
        ranges,
        timeline
      };
      let bucket = 'never';
      for (const ourBucket of ['current', 'future', 'past']) {
        if (R.path([type, ourBucket, 'startDate'], timeline)) {
          bucket = ourBucket;
          break;
        }
      }
      groupingMap[bucket].push(isci);
    }

    const colorMapInner = getColorMap(
      R.flatten(Object.values(groupingMap)).map(isci => ({ name: isci }))
    );

    return [builtCreativeMap, colorMapInner];
  }, [creativeData, streaming, mediaTypes]);

  useEffect(() => {
    if (refresh && setRefresh) {
      setRefresh(false);
      dispatch({
        type: SET_CREATIVE_MAP,
        company,
        data: undefined
      });
      setLoaded(false);
    }
  }, [company, dispatch, refresh, setRefresh]);

  useEffect(() => {
    if (!company) {
      return undefined;
    }
    if (creativeData) {
      setLoaded(true);
      return undefined;
    }

    let cancelled = false;
    const fetchKey = `${company}_${testParamsKey}`;
    const inFlight =
      inFlightCreativeFetches[fetchKey] ??
      (inFlightCreativeFetches[fetchKey] = (async () => {
        const res = await CreativeLambdaFetch('/', {
          params: {
            company,
            timeline: 1,
            include_retired: 1,
            ...stableTestParams
          }
        });
        return awaitJSON(res);
      })());

    inFlight
      .then(data => {
        if (cancelled) {
          return;
        }
        dispatch({
          type: SET_CREATIVE_MAP_IF_UNSET,
          company,
          data
        });
        setLoaded(true);
      })
      .catch(e => {
        if (cancelled) {
          return;
        }
        setError({
          message: `Couldn't fetch creative map. Error: ${e.message}`,
          reportError: e
        });
      })
      .finally(() => {
        delete inFlightCreativeFetches[fetchKey];
      });

    return () => {
      cancelled = true;
    };
  }, [company, creativeData, dispatch, setError, stableTestParams, testParamsKey]);

  return {
    creativeMap,
    colorMap,
    isciLiveOnDate: isciLiveOnDate(creativeMap),
    loaded
  };
};

const setCreativeMap = (
  state: CreativeReduxState,
  { data, company }: { data: CreativeData; company: string }
) => ({
  ...state,
  [company]: data
});

const setCreativeMapIfUnset = (
  state: CreativeReduxState,
  { data, company }: { data: CreativeData; company: string }
) => (R.isNil(state[company]) ? { ...state, [company]: data } : state);

export default makeReducer<CreativeReduxState>(
  {},
  {
    [SET_CREATIVE_MAP]: setCreativeMap,
    [SET_CREATIVE_MAP_IF_UNSET]: setCreativeMapIfUnset
  }
);
